/**
 * Notification Service
 *
 * Zentraler Service für alle Benachrichtigungen und Bestätigungen im System.
 * Nutzt SweetAlert2 für moderne, konsistente UI-Benachrichtigungen.
 *
 * Features:
 * - Toast-Benachrichtigungen (oben rechts)
 * - Popup-Dialoge mit Bestätigungen
 * - Loading-Indikatoren
 * - i18n-ready mit TranslateService
 */

import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal, { SweetAlertIcon, SweetAlertResult } from 'sweetalert2';

export interface ConfirmOptions {
  title?: string;
  text?: string;
  icon?: SweetAlertIcon;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
}

export interface ToastOptions {
  title?: string;
  icon?: SweetAlertIcon;
  timer?: number;
  position?: 'top' | 'top-end' | 'top-start' | 'center' | 'center-end' | 'center-start' | 'bottom' | 'bottom-end' | 'bottom-start';
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private translate = inject(TranslateService);

  /**
   * Zeigt eine Erfolgs-Toast-Benachrichtigung (oben rechts)
   */
  showSuccess(message: string, title?: string): void {
    const translatedMessage = this.translate.instant(message);
    const translatedTitle = title ? this.translate.instant(title) : this.translate.instant('COMMON.MESSAGES.SUCCESS');

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: translatedTitle,
      text: translatedMessage,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  /**
   * Zeigt eine Fehler-Toast-Benachrichtigung (oben rechts)
   */
  showError(message: string, title?: string): void {
    const translatedMessage = this.translate.instant(message);
    const translatedTitle = title ? this.translate.instant(title) : this.translate.instant('COMMON.MESSAGES.ERROR');

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'error',
      title: translatedTitle,
      text: translatedMessage,
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  /**
   * Zeigt eine Info-Toast-Benachrichtigung (oben rechts)
   */
  showInfo(message: string, title?: string): void {
    const translatedMessage = this.translate.instant(message);
    const translatedTitle = title ? this.translate.instant(title) : this.translate.instant('COMMON.MESSAGES.INFO');

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'info',
      title: translatedTitle,
      text: translatedMessage,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  /**
   * Zeigt eine Warnung-Toast-Benachrichtigung (oben rechts)
   */
  showWarning(message: string, title?: string): void {
    const translatedMessage = this.translate.instant(message);
    const translatedTitle = title ? this.translate.instant(title) : this.translate.instant('COMMON.MESSAGES.WARNING');

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'warning',
      title: translatedTitle,
      text: translatedMessage,
      showConfirmButton: false,
      timer: 3500,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  /**
   * Zeigt einen Bestätigungs-Dialog mit Ja/Nein Buttons
   * @returns Promise<boolean> - true wenn bestätigt, false wenn abgebrochen
   */
  async confirm(options: ConfirmOptions = {}): Promise<boolean> {
    const {
      title = 'COMMON.MESSAGES.CONFIRM',
      text = 'COMMON.MESSAGES.CONFIRM_ACTION',
      icon = 'question',
      confirmButtonText = 'COMMON.BUTTONS.YES',
      cancelButtonText = 'COMMON.BUTTONS.NO',
      confirmButtonColor = '#28a745',
      cancelButtonColor = '#dc3545'
    } = options;

    const result: SweetAlertResult = await Swal.fire({
      title: this.translate.instant(title),
      text: this.translate.instant(text),
      icon: icon,
      showCancelButton: true,
      confirmButtonColor: confirmButtonColor,
      cancelButtonColor: cancelButtonColor,
      confirmButtonText: this.translate.instant(confirmButtonText),
      cancelButtonText: this.translate.instant(cancelButtonText),
      reverseButtons: true,
      focusCancel: true
    });

    return result.isConfirmed;
  }

  /**
   * Zeigt einen Lösch-Bestätigungs-Dialog
   * @returns Promise<boolean> - true wenn bestätigt, false wenn abgebrochen
   */
  async confirmDelete(itemName?: string): Promise<boolean> {
    const text = itemName
      ? this.translate.instant('COMMON.MESSAGES.CONFIRM_DELETE_ITEM', { item: itemName })
      : this.translate.instant('COMMON.MESSAGES.CONFIRM_DELETE');

    return this.confirm({
      title: 'COMMON.MESSAGES.DELETE_TITLE',
      text: text,
      icon: 'warning',
      confirmButtonText: 'COMMON.BUTTONS.DELETE',
      cancelButtonText: 'COMMON.BUTTONS.CANCEL',
      confirmButtonColor: '#dc3545'
    });
  }

  /**
   * Zeigt einen Loading-Indikator mit Dark-Mode Support
   * Muss mit closeLoading() geschlossen werden
   *
   * @param message - i18n-Key für Nachricht (default: 'COMMON.MESSAGES.LOADING')
   *
   * @example
   * ```typescript
   * this.notificationService.showLoading('INVOICE.GENERATING');
   * // ... async operation
   * this.notificationService.closeLoading();
   * ```
   */
  showLoading(message?: string): void {
    const translatedMessage = message
      ? this.translate.instant(message)
      : this.translate.instant('COMMON.MESSAGES.LOADING');

    Swal.fire({
      title: translatedMessage,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-bg'),
      color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color'),
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  /**
   * Schließt den Loading-Indikator
   */
  closeLoading(): void {
    Swal.close();
  }

  /**
   * Zeigt ein Success-Overlay (kurz, automatisch schließend) mit Dark-Mode Support
   *
   * @param title - i18n-Key für Titel
   * @param text - i18n-Key oder direkter Text für Details (optional)
   * @param timer - Millisekunden bis Auto-Close (default: 2000)
   *
   * @example
   * ```typescript
   * await this.notificationService.showSuccessOverlay(
   *   'INVOICE.SUCCESS',
   *   'RE-2025-10-00042',
   *   2000
   * );
   * ```
   */
  async showSuccessOverlay(title: string, text?: string, timer: number = 2000): Promise<void> {
    const translatedTitle = this.translate.instant(title);
    const displayText = text
      ? (text.startsWith('COMMON.') || text.includes('.') ? this.translate.instant(text) : text)
      : undefined;

    await Swal.fire({
      icon: 'success',
      title: translatedTitle,
      text: displayText,
      timer: timer,
      showConfirmButton: false,
      background: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-bg'),
      color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color')
    });
  }

  /**
   * Zeigt ein Error-Overlay mit Dark-Mode Support
   *
   * @param title - i18n-Key für Titel
   * @param text - Error-Text (optional)
   *
   * @example
   * ```typescript
   * await this.notificationService.showErrorOverlay(
   *   'INVOICE.ERROR',
   *   error.message
   * );
   * ```
   */
  async showErrorOverlay(title: string, text?: string): Promise<void> {
    const translatedTitle = this.translate.instant(title);
    const displayText = text
      ? (text.startsWith('COMMON.') || text.includes('.') ? this.translate.instant(text) : text)
      : undefined;

    await Swal.fire({
      icon: 'error',
      title: translatedTitle,
      text: displayText,
      background: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-bg'),
      color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color')
    });
  }

  /**
   * Zeigt einen Custom Toast mit eigenen Optionen
   */
  showCustomToast(options: ToastOptions): void {
    const {
      title = 'Notification',
      icon = 'info',
      timer = 3000,
      position = 'top-end'
    } = options;

    Swal.fire({
      toast: true,
      position: position,
      icon: icon,
      title: this.translate.instant(title),
      showConfirmButton: false,
      timer: timer,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });
  }

  /**
   * Zeigt einen Custom Popup-Dialog
   */
  async showCustomDialog(options: {
    title: string;
    html?: string;
    text?: string;
    icon?: SweetAlertIcon;
    showCancelButton?: boolean;
    confirmButtonText?: string;
    cancelButtonText?: string;
  }): Promise<SweetAlertResult> {
    return await Swal.fire({
      title: this.translate.instant(options.title),
      text: options.text ? this.translate.instant(options.text) : undefined,
      html: options.html,
      icon: options.icon || 'info',
      showCancelButton: options.showCancelButton || false,
      confirmButtonText: options.confirmButtonText
        ? this.translate.instant(options.confirmButtonText)
        : this.translate.instant('COMMON.BUTTONS.OK'),
      cancelButtonText: options.cancelButtonText
        ? this.translate.instant(options.cancelButtonText)
        : this.translate.instant('COMMON.BUTTONS.CANCEL'),
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d'
    });
  }

  /**
   * Zeigt einen Info-Dialog mit langem Text
   * Nützlich für das Anzeigen von vollständigen Notizen oder Beschreibungen
   */
  showInfoText(title: string, text: string): void {
    Swal.fire({
      title: this.translate.instant(title),
      html: `<div style="text-align: left; max-height: 400px; overflow-y: auto; white-space: pre-wrap;">${text || '-'}</div>`,
      icon: 'info',
      confirmButtonText: this.translate.instant('COMMON.BUTTONS.CLOSE'),
      confirmButtonColor: '#6c757d',
      width: '600px',
      customClass: {
        popup: 'swal-info-text-popup'
      }
    });
  }
}
